import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { PaginationList } from '../PaginationList/index.esm.js';
import '@skatteetaten/ds-buttons';
import '@skatteetaten/ds-icons';
import './styles.css';


const getDefaultPageSize = ()=>10;
const getDefaultSibling = ()=>3;
const getDefaultHidePrevNextButtonTitle = ()=>false;
const getDefaultHidePageSummary = ()=>false;

var styles = {
    "pagination": "Pagination-module_pagination__teks-",
    "paginationList": "Pagination-module_paginationList__YgYv7",
    "paginationList_summaryhidden": "Pagination-module_paginationList_summaryhidden__EKp5o",
    "pagination_summaryhide": "Pagination-module_pagination_summaryhide__D4AeK"};

const validPropRanges = ({ currentPage, pageSize, totalItems })=>{
    if (totalItems < 1) {
        // Ingen elementer å vise.
        return false;
    } else if (Math.ceil(totalItems / pageSize) < currentPage) {
        console.log('Pagination: currentPage er høyere enn totalItems / pageSize avrundet opp');
        return false;
    } else if (currentPage <= 0) {
        console.log(`Pagination: currentPage må være fra og med 1 til og med ${Math.ceil(totalItems / pageSize)}.`);
        return false;
    } else if (pageSize <= 0) {
        console.log(`Pagination: pageSize er mindre enn 0.`);
        return false;
    }
    return true;
};
const Pagination = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, currentPage: externalCurrentPage, defaultCurrent = 1, sibling = getDefaultSibling(), pageSize = getDefaultPageSize(), totalItems, hidePrevNextButtonTitle = getDefaultHidePrevNextButtonTitle(), hidePageSummary = getDefaultHidePageSummary(), ariaLabel, onChange }, ref)=>{
    const { t } = useTranslation('ds_navigation', {
        i18n: dsI18n
    });
    const lastPageRef = useRef(null);
    const firstPageRef = useRef(null);
    const lastPage = Math.ceil(totalItems / pageSize);
    const pageSibling = sibling < 1 ? 1 : sibling;
    const [internalPage, setInternalPage] = useState(defaultCurrent);
    const currentPage = externalCurrentPage ?? internalPage;
    if (!validPropRanges({
        totalItems,
        currentPage,
        pageSize
    })) {
        return null;
    }
    const handleChange = (page)=>{
        setInternalPage(page);
        if (page === 1) {
            firstPageRef?.current?.focus();
        } else if (page === lastPage) {
            lastPageRef?.current?.focus();
        }
        onChange?.(page);
    };
    const rangeTo = currentPage * pageSize > totalItems ? totalItems : currentPage * pageSize;
    const showPaginationSummary = dsI18n.t('ds_navigation:pagination.PageSummary', {
        range: `${currentPage * pageSize + 1 - pageSize}–${rangeTo}`,
        total: totalItems
    });
    if (showPaginationSummary === '') {
        throw new Error('Feil! Mangler importert språk fra ds-core-utils?');
    }
    const pageSummary = `${hidePageSummary ? styles.pagination_summaryhide : ''}`.trim();
    const paginationCss = `${styles.pagination} ${className}`.trim();
    const listCss = `${styles.paginationList} ${hidePageSummary ? styles.paginationList_summaryhidden : ''}`.trim();
    return /*#__PURE__*/ jsxs("nav", {
        ref: ref,
        id: id,
        className: paginationCss,
        lang: lang,
        "data-testid": dataTestId,
        "aria-label": ariaLabel ?? t('pagination.WrapperAriaLabel'),
        children: [
            /*#__PURE__*/ jsx("div", {
                className: pageSummary,
                "aria-live": 'polite',
                "aria-atomic": 'true',
                children: showPaginationSummary
            }),
            /*#__PURE__*/ jsx("ul", {
                className: listCss,
                children: lastPage > 1 && /*#__PURE__*/ jsx(PaginationList, {
                    lastPage: lastPage,
                    sibling: pageSibling,
                    currentPage: currentPage,
                    handleChange: handleChange,
                    firstPageRef: firstPageRef,
                    lastPageRef: lastPageRef,
                    hidePrevNextButtonTitle: hidePrevNextButtonTitle
                })
            })
        ]
    });
});
Pagination.displayName = 'Pagination';
Pagination.List = PaginationList;
Pagination.List.displayName = 'Pagination.List';

export { Pagination, getDefaultHidePageSummary, getDefaultHidePrevNextButtonTitle, getDefaultPageSize, getDefaultSibling };
